/**
 * ericHistoryStore — módulo de servicio plano para persistir el historial de
 * conversación de ERIC Analyst en localStorage.
 *
 * Diseño deliberadamente plano (no un hook de React) para ser consumido por
 * el class component EricAnalyst sin pelear con la convención del proyecto.
 *
 * Responsabilidades:
 *   - push(pregunta, respuesta): agrega un par al historial (máx. 50 entradas).
 *   - clear(): vacía el historial.
 *   - getHistory(): devuelve el array de mensajes actual (copia defensiva).
 *   - subscribe(callback): registra un listener; devuelve una función unsubscribe.
 *
 * El módulo escucha el evento `storage` de window para sincronizar entre tabs.
 * Cuando otro tab escribe la clave `eric:history`, los suscriptores locales
 * son notificados con el historial actualizado.
 *
 * Sanitización: el campo `sql_generado` se omite de las entradas al persistir,
 * ya que puede contener queries potencialmente largas que no son necesarias
 * para rehidratar la UI.
 *
 * docker exec -it vecfleet-web-workspace-1 bash -c "cd /var/www && CI=true npm test -- --testPathPattern=ericHistoryStore"
 */

var STORAGE_KEY = 'eric:history';
var MAX_ENTRIES = 50;

var _listeners = [];

function _readFromStorage() {
  try {
    var raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    var parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function _writeToStorage(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (e) {
    // localStorage puede lanzar QuotaExceededError — no crashear el componente.
  }
}

function _sanitize(mensaje) {
  // Omitir sql_generado: puede ser largo y no aporta a la rehidratación de UI.
  var sanitized = {};
  var keys = Object.keys(mensaje);
  for (var i = 0; i < keys.length; i++) {
    if (keys[i] !== 'sql_generado') {
      sanitized[keys[i]] = mensaje[keys[i]];
    }
  }
  return sanitized;
}

function _notifyListeners(history) {
  for (var i = 0; i < _listeners.length; i++) {
    try {
      _listeners[i](history);
    } catch (e) {
      // Un listener roto no debe cortar la cadena.
    }
  }
}

/**
 * Agrega un par pregunta/respuesta al historial persistido.
 * Si ya hay 50 entradas, descarta la más antigua (FIFO).
 *
 * @param {object} mensajeUsuario  - objeto mensaje con tipo 'usuario'
 * @param {object} mensajeAsistente - objeto mensaje con tipo 'asistente'
 */
function push(mensajeUsuario, mensajeAsistente) {
  var entries = _readFromStorage();
  entries.push(_sanitize(mensajeUsuario));
  entries.push(_sanitize(mensajeAsistente));

  // Mantener el límite: cada par ocupa 2 slots, así que cortamos por mensajes.
  if (entries.length > MAX_ENTRIES * 2) {
    entries = entries.slice(entries.length - MAX_ENTRIES * 2);
  }

  _writeToStorage(entries);
  _notifyListeners(entries.slice());
}

/**
 * Actualiza un mensaje del historial fusionando los campos en `partial` sobre
 * la entrada existente y persistiendo el resultado. Pensado para mutaciones
 * locales chicas (ej. opinion del usuario sobre una respuesta de ERIC) sin
 * tener que reescribir todo el historial.
 *
 * Si el índice está fuera de rango, no hace nada.
 *
 * @param {number} index   Índice del mensaje dentro del historial.
 * @param {object} partial Campos a fusionar sobre el mensaje existente.
 */
function updateMensaje(index, partial) {
  var entries = _readFromStorage();
  if (index < 0 || index >= entries.length) return;
  var existente = entries[index] || {};
  var merged = {};
  var k;
  for (k in existente) {
    if (Object.prototype.hasOwnProperty.call(existente, k)) merged[k] = existente[k];
  }
  for (k in partial) {
    if (Object.prototype.hasOwnProperty.call(partial, k)) merged[k] = partial[k];
  }
  entries[index] = _sanitize(merged);
  _writeToStorage(entries);
  _notifyListeners(entries.slice());
}

/**
 * Vacía el historial del localStorage y notifica a los suscriptores.
 */
function clear() {
  localStorage.removeItem(STORAGE_KEY);
  _notifyListeners([]);
}

/**
 * Devuelve una copia defensiva del historial actual.
 *
 * @returns {Array}
 */
function getHistory() {
  return _readFromStorage();
}

/**
 * Registra un callback que se invoca cuando el historial cambia.
 * Soporta cambios desde el tab actual y desde otros tabs (evento `storage`).
 *
 * @param {function} callback - function(history: Array) => void
 * @returns {function} unsubscribe — invocar en componentWillUnmount
 */
function subscribe(callback) {
  _listeners.push(callback);

  function onStorageEvent(event) {
    if (event.key === STORAGE_KEY) {
      var history = _readFromStorage();
      callback(history);
    }
  }

  window.addEventListener('storage', onStorageEvent);

  return function unsubscribe() {
    _listeners = _listeners.filter(function(l) {
      return l !== callback;
    });
    window.removeEventListener('storage', onStorageEvent);
  };
}

var ericHistoryStore = {
  push: push,
  updateMensaje: updateMensaje,
  clear: clear,
  getHistory: getHistory,
  subscribe: subscribe,
};

export default ericHistoryStore;
